import { Api, isAdmin } from "@model";
import { Button, Checkbox, Empty, Form, Input, Modal, Popconfirm, Spin, Table, Tag, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIsCompact } from "../../../hooks/useIsCompact";

type PanelAdmin = {
  id: string;
  username: string;
  name?: string;
  email?: string;
  role: "admin" | "manager";
  permissions: string[];
};

const permissionGroups = [
  ["Dashboard", ["dashboard.view"]],
  [
    "Users",
    [
      "users.view",
      "users.edit",
      "users.delete",
      "users.messages",
      "users.beta-features",
      "users.send-notification",
    ],
  ],
  [
    "Characters",
    [
      "characters.view",
      "characters.create",
      "characters.edit",
      "characters.delete",
      "characters.delete-own",
    ],
  ],
  ["Reports", ["reports.view", "reports.edit", "reports.delete"]],
  ["Payments", ["payments.view"]],
  ["Words", ["words.view", "words.edit", "words.delete"]],
  [
    "Word cards",
    [
      "word-cards.view",
      "word-cards.create",
      "word-cards.edit",
      "word-cards.delete",
      "word-cards.delete-own",
    ],
  ],
  ["Notifications", ["notifications.view", "notifications.create"]],
  [
    "Surveys",
    ["surveys.view", "surveys.create", "surveys.edit", "surveys.delete"],
  ],
  [
    "Chat lessons",
    [
      "chat-lessons.view",
      "chat-lessons.create",
      "chat-lessons.edit",
      "chat-lessons.delete",
      "chat-lessons.delete-own",
    ],
  ],
  [
    "Journeys",
    [
      "journeys.view",
      "journeys.create",
      "journeys.edit",
      "journeys.delete",
      "journeys.delete-own",
      "journeys.logs",
    ],
  ],
  ["Settings", ["settings.view", "settings.edit"]],
  ["Files", ["files.view", "files.create", "files.delete"]],
  ["Backups", ["backups.view", "backups.create"]],
  ["Course templates", ["course-templates.view", "course-templates.create", "course-templates.edit", "course-templates.delete"]],
  ["Error logs", ["error-logs.view", "error-logs.delete"]],
  ["Chats", ["chats.view"]],
] as const;

const labelForPermission = (permission: string) =>
  permission
    .split(".")[1]
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function ManagerSettings() {
  const isCompact = useIsCompact();
  const [admins, setAdmins] = useState<PanelAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PanelAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Api.get("/api/admin/panel/managers");
      setAdmins(response.data?.data ?? []);
    } catch {
      message.error("Failed to load administrators");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin()) void loadAdmins();
  }, [loadAdmins]);

  if (!isAdmin()) return null;

  const showModal = (admin?: PanelAdmin) => {
    setEditing(admin ?? null);
    form.setFieldsValue({
      username: admin?.username,
      name: admin?.name,
      email: admin?.email,
      permissions: admin?.permissions ?? [],
      password: undefined,
    });
    setOpen(true);
  };

  const saveManager = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        password: values.password || undefined,
      };
      if (editing) {
        await Api.put(`/api/admin/panel/managers/${editing.id}`, payload);
      } else {
        await Api.post("/api/admin/panel/managers", payload);
      }
      message.success(`${editing?.role === "admin" ? "Admin" : "Manager"} ${editing ? "updated" : "created"}`);
      setOpen(false);
      form.resetFields();
      loadAdmins();
    } catch (error: any) {
      message.error(error?.response?.data?.error?.[0] ?? "Failed to save manager");
    } finally {
      setSaving(false);
    }
  };

  const deleteManager = async (manager: PanelAdmin) => {
    try {
      await Api.delete(`/api/admin/panel/managers/${manager.id}`);
      message.success("Manager deleted");
      loadAdmins();
    } catch {
      message.error("Failed to delete manager");
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: isCompact ? "stretch" : "flex-end", marginBottom: 16 }}>
        <Button type="primary" block={isCompact} onClick={() => showModal()}>New manager</Button>
      </div>
      {isCompact ? (
        <Spin spinning={loading}>
          {admins.length === 0 && !loading ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No managers" />
          ) : (
            admins.map((admin) => (
              <div
                key={admin.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{admin.username}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{admin.name || "-"}</div>
                    <div style={{ fontSize: 12, color: "#888", wordBreak: "break-all" }}>
                      {admin.email || "-"}
                    </div>
                  </div>
                  <Tag color={admin.role === "admin" ? "blue" : "default"}>{admin.role}</Tag>
                </div>
                <div style={{ marginTop: 8 }}>
                  {admin.role === "admin" ? (
                    <Tag color="blue">Full access</Tag>
                  ) : admin.permissions?.length ? (
                    <Tag>{admin.permissions.length} permissions</Tag>
                  ) : (
                    <Tag>None</Tag>
                  )}
                </div>
                <div>
                  <Button type="link" style={{ paddingLeft: 0 }} onClick={() => showModal(admin)}>
                    Edit
                  </Button>
                  {admin.role !== "admin" && (
                    <Popconfirm
                      title={`Delete ${admin.username}?`}
                      onConfirm={() => deleteManager(admin)}
                    >
                      <Button danger type="link">Delete</Button>
                    </Popconfirm>
                  )}
                </div>
              </div>
            ))
          )}
        </Spin>
      ) : (
      <Table
        rowKey="id"
        loading={loading}
        dataSource={admins}
        pagination={false}
        columns={[
          { title: "Username", dataIndex: "username" },
          { title: "Name", dataIndex: "name", render: (value) => value || "-" },
          { title: "Email", dataIndex: "email", render: (value) => value || "-" },
          {
            title: "Role",
            dataIndex: "role",
            render: (role: PanelAdmin["role"]) => (
              <Tag color={role === "admin" ? "blue" : "default"}>{role}</Tag>
            ),
          },
          {
            title: "Permissions",
            dataIndex: "permissions",
            render: (permissions: string[], admin: PanelAdmin) => admin.role === "admin"
              ? <Tag color="blue">Full access</Tag>
              : permissions?.length
              ? <Tag>{permissions.length} assigned</Tag>
              : <Tag color="default">None</Tag>,
          },
          {
            title: "Actions",
            render: (_value, admin: PanelAdmin) =>
              admin.role === "admin" ? (
                <Button type="link" onClick={() => showModal(admin)}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button type="link" onClick={() => showModal(admin)}>Edit</Button>
                  <Popconfirm
                    title={`Delete ${admin.username}?`}
                    onConfirm={() => deleteManager(admin)}
                  >
                    <Button danger type="link">Delete</Button>
                  </Popconfirm>
                </>
              ),
          },
        ]}
      />
      )}
      <Modal
        title={
          editing?.role === "admin"
            ? `Edit ${editing.username}`
            : editing
              ? "Edit manager"
              : "New manager"
        }
        visible={open}
        okText={editing ? "Save" : "Create"}
        confirmLoading={saving}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={isCompact ? "100%" : 760}
      >
        <Form form={form} layout="vertical" onFinish={saveManager}>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name"><Input /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: "email" }]}><Input /></Form.Item>
          <Form.Item
            name="password"
            label={editing ? "New password (optional)" : "Password"}
            rules={editing ? [] : [{ required: true, min: 8 }]}
          >
            <Input.Password />
          </Form.Item>
          {editing?.role !== "admin" && <Form.Item name="permissions" label="Permissions">
            <Checkbox.Group style={{ width: "100%" }}>
              {permissionGroups.map(([title, permissions]) => (
                <div key={title} style={{ marginBottom: 12 }}>
                  <strong>{title}</strong>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                    {permissions.map((permission) => (
                      <Checkbox key={permission} value={permission}>
                        {labelForPermission(permission)}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>}
        </Form>
      </Modal>
    </>
  );
}
